/**
 * Cookie consent state management
 *
 * Zustand store persisted to localStorage.
 * Three states: undecided | accepted | declined
 *
 * When consent is accepted, GA is initialized.
 * When consent is declined (or revoked), GA is torn down.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { initGA, teardownGA } from './gtag';

export type ConsentStatus = 'undecided' | 'accepted' | 'declined';

interface ConsentState {
  /** Current consent status */
  status: ConsentStatus;

  /** Accept analytics cookies — initializes GA */
  accept: () => void;

  /** Decline analytics cookies — tears down GA */
  decline: () => void;

  /** Reset consent (show banner again) */
  reset: () => void;
}

export const useConsentStore = create<ConsentState>()(
  persist(
    (set) => ({
      status: 'undecided',

      accept: () => {
        initGA();
        set({ status: 'accepted' });
      },

      decline: () => {
        teardownGA();
        set({ status: 'declined' });
      },

      reset: () => {
        teardownGA();
        set({ status: 'undecided' });
      },
    }),
    {
      name: 'haive-cookie-consent',
      // On rehydration, re-initialize GA if consent was previously accepted
      onRehydrateStorage: () => (state) => {
        if (state?.status === 'accepted') {
          initGA();
        }
      },
    },
  ),
);
